// pages/SelfCoding/CreateEditSelfCoding.tsx
import { t } from "@lingui/macro";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  Step,
  Stepper,
  StepLabel,
  Grid,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alerts from "@/client/components/alerts";
import { useUser } from "@/client/services/user";

const CreateEditSelfCoding = () => {
  const { id, step: stepParam } = useParams<{ id: string; step: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const studentId = user?.id;

  const [currentStep, setCurrentStep] = useState(0);
  const [codingId, setCodingId] = useState<string | null>(id || null);
  const [coding, setCoding] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    time: 10,
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const isEditMode = !!id;
  const steps = [t`Coding Assessment Details`, t`Add Questions`, t`Publish`];

  useEffect(() => {
    if (stepParam) {
      setCurrentStep(parseInt(stepParam) || 0);
    }
    if (id) {
      fetchCoding();
    }
  }, [id, stepParam]);

  const fetchCoding = async () => {
    try {
      const res = await axios.get(
        `/api/integration/external-service/self-coding/${id}`,
        { params: { studentId } }
      );
      const codingData = res.data.result;
      setCoding(codingData);
      setCodingId(codingData._id);
      setFormData({
        name: codingData.name || "",
        description: codingData.description || "",
        category: codingData.category || "",
        time: codingData.time || 10,
      });
    } catch (error) {
      console.error("Error fetching self-coding assessment:", error);
      setAlert({
        open: true,
        message: t`Failed to fetch Coding assessment details`,
        severity: "error",
      });
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateCoding = async () => {
    if (!formData.name.trim()) {
      setAlert({
        open: true,
        message: t`Coding Assessment name is required`,
        severity: "warning",
      });
      return;
    }

    if (!formData.category) {
      setAlert({
        open: true,
        message: t`Coding Assessment category is required`,
        severity: "warning",
      });
      return;
    }

    if (!formData.time) {
      setAlert({
        open: true,
        message: t`Time limit is required`,
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        time: Number(formData.time),
        studentId,
      };

      if (isEditMode && id) {
        // Update existing
        await axios.patch(
          `/api/integration/external-service/self-coding/${id}`,
          payload,
          { params: { studentId } }
        );
        setAlert({
          open: true,
          message: t`Coding Assessment updated successfully`,
          severity: "success",
        });
        setTimeout(() => {
          navigate(`/self-coding/create/${id}/1`);
          setCurrentStep(1);
        }, 500);
        return;
      } else {
        // Create new
        const res = await axios.post(
          `/api/integration/external-service/self-coding`,
          payload
        );
        const newId = res.data.result._id;
        setCodingId(newId);
        setAlert({
          open: true,
          message: t`Coding Assessment created successfully`,
          severity: "success",
        });
        setTimeout(() => {
          navigate(`/self-coding/create/${newId}/1`);
          setCurrentStep(1);
        }, 500);
        return;
      }
    } catch (error: any) {
      console.error("Error saving Coding assessment:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to save Coding assessment`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      handleCreateCoding();
    } else if (currentStep === 1) {
      navigate(`/self-coding/create/${codingId}/2`);
      setCurrentStep(2);
    }
  };

  const handleSaveAsDraft = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        time: Number(formData.time),
        studentId,
      };
      await axios.patch(
        `/api/integration/external-service/self-coding/${codingId}`,
        payload,
        { params: { studentId } }
      );
      setAlert({
        open: true,
        message: t`Coding Assessment saved as draft`,
        severity: "success",
      });
      setTimeout(() => {
        navigate("/self-coding");
      }, 1500);
    } catch (error: any) {
      console.error("Error saving Coding assessment:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to save Coding assessment`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        time: Number(formData.time),
        studentId,
        status: "published",
      };
      await axios.patch(
        `/api/integration/external-service/self-coding/${codingId}`,
        payload,
        { params: { studentId } }
      );
      setAlert({
        open: true,
        message: t`Coding Assessment published successfully`,
        severity: "success",
      });
      setTimeout(() => {
        navigate("/self-coding");
      }, 1500);
    } catch (error: any) {
      console.error("Error publishing Coding assessment:", error);
      setAlert({
        open: true,
        message:
          error.response?.data?.error || t`Failed to publish Coding assessment`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      navigate(`/self-coding/create/${codingId}/${prevStep}`);
      setCurrentStep(prevStep);
    } else {
      navigate("/self-coding");
    }
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "info" });
  };

  return (
    <Box>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/self-coding")}
            sx={{ textTransform: "none" }}
          >
            {t`Back to Self-Coding`}
          </Button>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {/* Step 0: Assessment Details */}
            {currentStep === 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                  {isEditMode
                    ? t`Edit Coding Test Details`
                    : t`Create Coding Test`}
                </Typography>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t`Coding Test Name`}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t`e.g., JavaScript Coding Challenge`}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 48,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                    
                  />

                  <FormControl fullWidth size="small" required>
                    <InputLabel>{t`Coding Test Category`}</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      label={t`Coding Test Category`}
                      required
                    >
                      <MenuItem value="practice">{t`Practice`}</MenuItem>
                      <MenuItem value="final">{t`Final`}</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    size="small"
                    label={t`Time Limit (minutes)`}
                    name="time"
                    type="number"
                    value={formData.time}
                    onChange={handleChange}
                    inputProps={{ min: 1 }}
                    required
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 48,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999 !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#555 !important",
                        },
                      "& .MuiOutlinedInput-root.Mui-focused": {
                        boxShadow: "none !important",
                        outline: "none !important",
                      },
                      "& input:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label={t`Description`}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder={t`Brief description of what this Coding Test covers`}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#555",
                        },
                      },
                      "& .MuiInputBase-inputMultiline:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        border: "none !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused textarea": {
                        outline: "none !important",
                      },
                    }}
                  />
                </Stack>
              </>
            )}

            {/* Step 1: Add Questions */}
            {currentStep === 1 && (
              <>
                {isEditMode && coding?.questions?.length > 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", fontSize: "17px" }}
                      >
                        {coding?.questions?.length} {` Questions`}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ background: "white", textTransform: "none" }}
                        onClick={() =>
                          navigate(`/self-coding/questions/${codingId}`)
                        }
                      >
                        {`Manage Questions`}
                      </Button>
                    </Box>

                    <CardContent>
                      <Stack spacing={1} sx={{ maxHeight: 300, overflow: "auto" }}>
                        {coding?.questions?.map(
                          (question: any, index: number) => (
                            <Box
                              key={question._id}
                              sx={{
                                px: 2,
                                py: 1.5,
                                bgcolor: "white",
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  gap: 2,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  {/* <Typography variant="body2">
                                    {index + 1}. {question.question?.text || ""}
                                  </Typography> */}
                                  <Typography variant="body2">
                                  {index + 1}. {question.question?.text || question.title || ""}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )
                        )}
                      </Stack>
                    </CardContent>
                  </>
                )}

                {!isEditMode || !coding?.questions?.length ? (
                  <Box
                    sx={{
                      p: 4,
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      textAlign: "center",
                      border: "2px dashed #ccc",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ mb: 3, color: "text.secondary" }}
                    >
                      {isEditMode
                        ? `Add questions to enhance your Coding assessment`
                        : `Click the button below to start adding questions`}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ textTransform: "none" }}
                      onClick={() =>
                        navigate(`/self-coding/questions/${codingId}`)
                      }
                    >
                      {`Add Questions`}
                    </Button>
                  </Box>
                ) : (
                  <Box>{/* Optional: Add any additional content here */}</Box>
                )}
              </>
            )}

            {/* Step 2: Publish */}
            {currentStep === 2 && (
              <>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
                  {t`Review and Publish`}
                </Typography>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Coding Test Name`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {formData.name}
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Category`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {formData.category}
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Time Limit`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {formData.time} {t`minutes`}
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Description`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {formData.description}
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t`Questions Added`}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          {coding?.questions?.length || 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                {currentStep === 0 ? t`Cancel` : t`Back`}
              </Button>

              {currentStep === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextStep}
                  disabled={loading || !formData.name.trim()}
                  sx={{ textTransform: "none" }}
                >
                  {isEditMode ? t`Update and Next` : t`Next`}
                </Button>
              )}

              {currentStep === 1 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextStep}
                  disabled={loading}
                  sx={{ textTransform: "none" }}
                >
                  {t`Next`}
                </Button>
              )}

              {currentStep === 2 && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleSaveAsDraft}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    {t`Save as Draft`}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PublishIcon />}
                    onClick={handlePublish}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    {t`Publish`}
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateEditSelfCoding;